import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Phone, Mail, Award, ShieldCheck, ThumbsUp, ArrowRight } from 'lucide-react'

export default function Sproejtemaling() {
  // SEO Focus
  useEffect(() => {
    document.title = 'Professionel sprøjtemaling i Østjylland | Malerfirma Villy Poulsen'
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute(
        'content',
        'Sprøjtemaling af døre, lofter, møbler, inventar og meget mere. Få et glat, ensartet og professionelt resultat med høj holdbarhed.'
      )
    }
  }, [])

  const sproejteYdelser = [
    'Døre og dørkarme',
    'Vinduer',
    'Radiatorer',
    'Fodlister',
    'Skabslåger',
    'Møbler',
    'Vægge og lofter',
    'Beklædningsbrædder',
    'Inventar og træværk'
  ]

  return (
    <div className="bg-white">
      {/* Hero-sektion */}
      <section className="relative bg-[#20252C] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/f3517ae6-43d8-433f-aa89-d1fc1ddd2762.jpeg"
            alt="Professionel sprøjtemaling i Østjylland"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01529E]/90 text-white text-xs font-bold uppercase tracking-widest mb-6">
              Specialiseret Håndværk
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Professionel sprøjtemaling i Østjylland
            </h1>
            <p className="mt-4 text-xl text-[#ED1C26] font-bold">
              Sprøjtemaling af døre, lofter, møbler, inventar og meget mere
            </p>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">
              Ønsker du et flot, ensartet og professionelt resultat? Hos Malerfirma Villy Poulsen ApS tilbyder vi professionel sprøjtemaling i hele Østjylland til både private og erhverv.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 rounded-xl bg-[#01529E] text-white font-bold text-center hover:bg-[#01529E]/90 transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer"
              >
                Kontakt os i dag
              </Link>
              <a
                href="tel:40637670"
                className="px-6 py-3.5 rounded-xl bg-white/10 text-white font-bold text-center hover:bg-white/20 transition-all border border-white/10 flex items-center justify-center gap-2"
              >
                Ring 40 63 76 70
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Flot og ensartet finish */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Moderne Finish
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Sprøjtemaling med moderne finish og høj holdbarhed
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Vi har stor erfaring med sprøjtemaling af blandt andet døre, vinduer, lofter, vægge, møbler og skabslåger. Sprøjtemaling er en effektiv løsning, når du ønsker en glat og flot overflade med et moderne finish og høj holdbarhed.
                </p>
                <p>
                  Uanset om du vil forny dit hjem, opdatere inventar eller give dine overflader nyt liv, sørger vi for et resultat i høj kvalitet. Vi arbejder med professionelle sprøjtesystemer og kvalitetsprodukter, som sikrer et flot og ensartet resultat hver gang.
                </p>
              </div>
            </div>

            <div>
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/spra-kdem_1.jpg"
                alt="Sprøjtemaling af skabslåger"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Grundig forberedelse */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/sp_1.jpg"
                alt="Sprøjtemaling af dør"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[400px]"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Præcision & Forberedelse
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Effektivt arbejde med stor præcision
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Inden arbejdet går i gang, sørger vores erfarne malere altid for grundig afdækning og forberedelse, så arbejdet udføres effektivt og med stor præcision.
                </p>
                <p>
                  Vi anvender vandbaseret maling af høj kvalitet, som giver en slidstærk og flot overflade.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Vi udfører sprøjtemaling af */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
              Vores Kompetencer
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4">
              Vi udfører sprøjtemaling af
            </h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Vi tilbyder professionel sprøjtemaling af en lang række emner for både private og erhverv.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sproejteYdelser.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl border-l-4 border-l-[#01529E] border-y border-r border-gray-100 p-5 flex items-center gap-4 shadow-xs">
                <div>
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">{item}</h3>
                  <p className="text-xs text-gray-500 mt-1">Professionel sprøjtelakering</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion: Fordele ved sprøjtemaling */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
            Hvorfor sprøjtemale?
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
            Elegant resultat uden synlige penselstrøg
          </h2>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
            Sprøjtemaling er en populær løsning, fordi det giver et elegant og professionelt udtryk uden synlige penselstrøg. Samtidig er det ofte en hurtig og effektiv metode, som kan være ideel til både renovering og ny opfriskning.
          </p>
        </div>
      </section>

      {/* CTA-sektion */}
      <section className="bg-[#20252C] text-white py-16 relative overflow-hidden">
        <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 w-96 h-96 bg-[#01529E]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            Få et tilbud på sprøjtemaling
          </h2>
          <p className="text-gray-300 text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Kontakt Malerfirma Villy Poulsen ApS for en uforpligtende samtale og et konkurrencedygtigt tilbud. Vi hjælper gerne med både små og store opgaver og står klar med rådgivning, kvalitetshåndværk og en løsning tilpasset dine ønsker.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm">
            <a href="tel:40637670" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
              <Phone className="h-5 w-5 text-[#ED1C26]" />
              Telefon: 40 63 76 70
            </a>
            <a href="mailto:mail@malerfirma-villy-poulsen.dk" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
              <Mail className="h-5 w-5 text-[#ED1C26]" />
              E-mail: mail@malerfirma-villy-poulsen.dk
            </a>
          </div>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#01529E] text-white font-bold hover:bg-[#01529E]/90 transition-all shadow-lg cursor-pointer"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
